import type {ProyectoFormDTO} from '../../../api/Objetos';
import {normalizeProyectoForm} from './projectFormUtils';

const STORAGE_PREFIX = 'admin-project-draft:';

export function buildProjectDraftKey(value: string) {
    return `${STORAGE_PREFIX}${value}`;
}

export function readProjectDraft(key: string): ProyectoFormDTO | null {
    if (typeof window === 'undefined') return null;

    try {
        const raw = window.sessionStorage.getItem(key);
        if (!raw) return null;
        return normalizeProyectoForm(JSON.parse(raw) as ProyectoFormDTO);
    } catch {
        return null;
    }
}

export function writeProjectDraft(key: string, value: ProyectoFormDTO, force = false) {
    if (typeof window === 'undefined') return;

    try {
        const normalized = normalizeProyectoForm(value, force);
        window.sessionStorage.setItem(key, JSON.stringify(normalized));
    } catch {
        // Ignore storage errors and keep the form usable.
    }
}

export function clearProjectDraft(key: string) {
    if (typeof window === 'undefined') return;

    try {
        window.sessionStorage.removeItem(key);
    } catch {
        // Ignore storage errors.
    }
}

export function getValueAtPath(source: unknown, path: string) {
    const pathParts = path.replace(/\[(\w+)\]/g, '.$1').split('.');
    return pathParts.reduce<unknown>((acc, part) => {
        if (!acc || typeof acc !== 'object') return undefined;
        return (acc as Record<string, unknown>)[part];
    }, source);
}

export function setValueAtPath<T>(source: T, path: string, nextValue: unknown): T {
    const pathParts = path.replace(/\[(\w+)\]/g, '.$1').split('.');
    const clone = deepClone(source) as Record<string, unknown>;
    let cursor: Record<string, unknown> = clone;

    for (let index = 0; index < pathParts.length - 1; index += 1) {
        const key = pathParts[index];
        const current = cursor[key];
        const next =
            current && typeof current === 'object' && !Array.isArray(current)
                ? {...(current as Record<string, unknown>)}
                : {};
        cursor[key] = next;
        cursor = next;
    }

    cursor[pathParts[pathParts.length - 1]] = nextValue;
    return clone as T;
}

function deepClone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
}
